/**
 * Microsoft Dataverse Web API client for desktop flows (RPA).
 *
 * Uses only the documented desktop-flow programmatic surface: the workflow
 * table (category 6) with its inputs/outputs file columns, the RunDesktopFlow
 * and CancelDesktopFlowRun bound actions, the flowsession table for run
 * monitoring, and the connectionreference table for connection discovery.
 * Reference: https://learn.microsoft.com/en-us/power-automate/developer/desktop-flow-public-apis
 *
 * Deliberately NOT here: anything on api.flow.microsoft.com (Microsoft marks
 * that plane unsupported) and desktop flow definition writes (no supported
 * authoring surface exists — the PAD designer and solution import are the
 * only paths).
 */

import { requestWithRetry, readResponseBody } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  ValidationError,
  wrapApiCall,
} from "../utils/errors.js";
import { sanitizeErrorMessage, validateDataverseUrl, validateRowId } from "../utils/security.js";
import { logger } from "../utils/logger.js";

const API_VERSION = "v9.2";

/** RunDesktopFlow rejects inputs above 2 MB (documented). */
const MAX_RUN_INPUTS_BYTES = 2 * 1024 * 1024;
/** RunDesktopFlow timeout bounds: default 3 h, documented max 24 h. */
const MAX_RUN_TIMEOUT_SECONDS = 24 * 60 * 60;

/** flowsession statuscode → meaning (documented on the flowsession entity reference). */
export const FLOW_SESSION_STATUS: Readonly<Record<number, string>> = {
  0: "NotSpecified",
  1: "Paused",
  2: "Running",
  3: "Waiting",
  4: "Succeeded",
  5: "Skipped",
  6: "Suspended",
  7: "Cancelled",
  8: "Failed",
  9: "Faulted",
  10: "TimedOut",
  11: "Aborted",
  12: "Ignored",
  13: "Deleted",
  14: "Terminated",
};

/** Statuses that mean the run is over (outputs/errors are final). */
export const TERMINAL_SESSION_STATUSES: ReadonlySet<number> = new Set([
  4, 5, 7, 8, 9, 10, 11, 12, 13, 14,
]);

/**
 * Documented run-time license-check codes returned by RunDesktopFlow, mapped
 * to what the user should actually do about them.
 * https://learn.microsoft.com/en-us/troubleshoot/power-platform/power-automate/desktop-flows/desktop-flow-run-time-license-check
 */
export const LICENSE_CHECK_GUIDANCE: Readonly<Record<string, string>> = {
  AttendedNoLicense:
    "Attended desktop flow runs require a Power Automate Premium user license on the owner of the desktop flow connection.",
  UnattendedNoAddon:
    "Unattended desktop flow runs require a Power Automate Process license allocated to the target machine (legacy name: Unattended RPA add-on), in addition to a Premium license on the connection owner.",
  LicensingCheckFailed:
    "Microsoft could not verify licensing for this run. This is usually transient — retry, then check the connection owner's license assignment.",
};

/** flowmachine statecode → meaning (documented on the flowmachine entity reference). */
export const MACHINE_STATE: Readonly<Record<number, string>> = {
  0: "Active",
  1: "Inactive",
  2: "Maintenance",
};

/** flowmachine statuscode → meaning (documented on the flowmachine entity reference). */
export const MACHINE_STATUS: Readonly<Record<number, string>> = {
  1: "Active",
  2: "Inactive",
  3: "RequiresReconnection",
  4: "ManualMaintenance",
  5: "DrainMode",
  6: "ToDelete",
  7: "Temporary",
  8: "Error",
  9: "Disabled",
  10: "Provisioning",
  11: "RequiresGroupKey",
  12: "ProvisionedWithError",
  13: "HostedMachineOvercapacity",
  14: "HostedMachineOvercapacityDeleted",
  15: "HostedMachineOvercapacityDisabled",
};

/** flowmachine hostingtype → meaning. Absent/0 = a physical (customer) machine. */
export const MACHINE_HOSTING_TYPE: Readonly<Record<number, string>> = {
  1: "Hosted",
  2: "Cloud PC",
};

/** flowmachinegroup flowgrouptype → meaning (documented option set). */
export const MACHINE_GROUP_TYPE: Readonly<Record<number, string>> = {
  545940000: "Keyless",
  545940001: "Standard",
  545940002: "Default",
};

/** flowmachine keydeliverystatus → meaning (group-key exchange health). */
export const KEY_DELIVERY_STATUS: Readonly<Record<number, string>> = {
  1: "Default",
  2: "PendingNewKey",
  3: "KeyExpired",
};

/** workqueueitem statecode → meaning (documented on the workqueueitem entity reference). */
export const WORK_QUEUE_ITEM_STATE: Readonly<Record<number, string>> = {
  0: "Queued",
  1: "Processing",
  2: "Processed",
  3: "OnHold",
  4: "Error",
};

/** workqueueitem statuscode → meaning (documented on the workqueueitem entity reference). */
export const WORK_QUEUE_ITEM_STATUS: Readonly<Record<number, string>> = {
  0: "Queued",
  1: "Processing",
  2: "Processed",
  3: "Paused",
  4: "GenericException",
  5: "ITException",
  6: "BusinessException",
  7: "DeadLetter",
  8: "ProcessingTimeout",
};

/** workqueue statuscode → meaning. */
export const WORK_QUEUE_STATUS: Readonly<Record<number, string>> = {
  1: "Active",
  2: "Inactive",
  3: "Paused",
};

/**
 * Terminal item results the update tool accepts, mapped to the documented
 * statecode/statuscode pairs (the same values PAD's "Process work queue items"
 * and the cloud-flow walkthrough write).
 */
export const WORK_QUEUE_ITEM_RESULTS = {
  processed: { statecode: 2, statuscode: 2 },
  generic_exception: { statecode: 4, statuscode: 4 },
  it_exception: { statecode: 4, statuscode: 5 },
  business_exception: { statecode: 4, statuscode: 6 },
  // Requeue is documented as a PAD action; the API equivalent is the item
  // back to Queued (optionally with delayuntil). Bounded server-side by the
  // queue's itemmaxrequeuecount.
  requeue: { statecode: 0, statuscode: 0 },
} as const;

export type WorkQueueItemResult = keyof typeof WORK_QUEUE_ITEM_RESULTS;

/** Documented cap on a work-queue item's input payload. */
const MAX_ITEM_INPUT_BYTES = 1024 * 1024;

export interface DesktopFlowsApiConfig {
  authProvider: AuthProvider;
  environmentUrl: string;
}

export interface DesktopFlowSummary {
  workflowid: string;
  name: string;
  description?: string;
  modifiedon?: string;
  statecode?: number;
  [key: string]: unknown;
}

export interface DesktopFlowDetail {
  flow: DesktopFlowSummary;
  /** Parsed JSON schema of the flow's input variables, when declared. */
  inputSchema?: unknown;
  /** Parsed JSON schema of the flow's output variables, when declared. */
  outputSchema?: unknown;
}

export interface RunDesktopFlowOptions {
  flowId: string;
  /** Desktop-flows connection name — or a connection reference logical name. */
  connectionName: string;
  /** Pass the connection value as a connection reference logical name (connectionType 2). */
  isConnectionReference?: boolean;
  runMode: "attended" | "unattended";
  /** Input variables; serialized to the JSON string the action expects. */
  inputs?: Record<string, unknown>;
  timeoutSeconds?: number;
  priority?: "normal" | "high";
  /** HTTPS endpoint Microsoft calls when the run completes (no localhost/IPs). */
  callbackUrl?: string;
}

export interface RunDesktopFlowResult {
  flowsessionId: string;
  /** Actionable message when the documented licenseCheck reports a problem. */
  licenseWarning?: string;
}

export interface FlowSessionSummary {
  flowsessionid: string;
  statuscode?: number;
  statecode?: number;
  startedon?: string;
  completedon?: string;
  errorcode?: string;
  errormessage?: string;
  errordetails?: string;
  gateway?: string;
  context?: string;
  [key: string]: unknown;
}

export interface FlowSessionDetail {
  session: FlowSessionSummary;
  /** Run outputs (JSON), fetched only when the run succeeded. */
  outputs?: unknown;
}

export interface DesktopFlowConnectionReference {
  connectionreferenceid: string;
  connectionreferencelogicalname?: string;
  connectionreferencedisplayname?: string;
  connectionid?: string;
  connectorid?: string;
  [key: string]: unknown;
}

export interface FlowMachine {
  flowmachineid: string;
  name?: string;
  statecode?: number;
  statuscode?: number;
  lastheartbeatdate?: string;
  sessioncapacity?: number;
  hostingtype?: number;
  agentversion?: string;
  _flowmachinegroupid_value?: string;
  hostedmachineerror?: string;
  keydeliverystatus?: number;
  overcapacitysince?: string;
  createdon?: string;
  modifiedon?: string;
  [key: string]: unknown;
}

export interface FlowMachineGroup {
  flowmachinegroupid: string;
  name?: string;
  description?: string;
  flowgrouptype?: number;
  statecode?: number;
  provisioningstate?: string;
  provisioningerror?: string;
  maxmanagedmachinecount?: number;
  createdon?: string;
  [key: string]: unknown;
}

export interface FlowMachineGroupWithCount extends FlowMachineGroup {
  /** Machines currently pointing at this group (counted, not a stored column). */
  machineCount: number;
}

export interface WorkQueue {
  workqueueid: string;
  name?: string;
  workqueuekey?: string;
  statecode?: number;
  statuscode?: number;
  itemmaxretrycount?: number;
  itemmaxrequeuecount?: number;
  defaultitemtimetoliveinminutes?: number;
  inputschematype?: number;
  workqueuetype?: number;
  createdon?: string;
  [key: string]: unknown;
}

/** Per-state item tallies for one queue (statecode buckets). */
export interface WorkQueueItemCounts {
  queued: number;
  processing: number;
  processed: number;
  onHold: number;
  error: number;
}

export interface WorkQueueItem {
  workqueueitemid: string;
  name?: string;
  statecode?: number;
  statuscode?: number;
  input?: string;
  priority?: number;
  processingresult?: string;
  retrycount?: number;
  requeuecount?: number;
  processingstarttime?: string;
  completedon?: string;
  expirydate?: string;
  delayuntil?: string;
  uniqueidbyqueue?: string;
  _workqueueid_value?: string;
  [key: string]: unknown;
}

export interface CreateWorkQueueOptions {
  name: string;
  /** Unique key; defaults to the name when omitted. */
  workqueueKey?: string;
  itemMaxRetryCount?: number;
  itemMaxRequeueCount?: number;
  defaultItemTimeToLiveMinutes?: number;
  /** JSON schema enforced against item inputs at enqueue time. */
  inputSchema?: Record<string, unknown>;
}

export interface FlowLogEntry {
  flowlogid?: string;
  createdon?: string;
  level?: number;
  type?: number;
  data?: string;
  [key: string]: unknown;
}

/**
 * Action-level logs for one run, from whichever documented source answered.
 * V2 (`flowlog` elastic table) is documented for connector-triggered runs;
 * API-triggered runs often only have the V1 `additionalcontext` file column.
 */
export interface RunLogsResult {
  source: "flowlog" | "additionalcontext" | "none";
  entries?: FlowLogEntry[];
  raw?: string;
}

export interface EnqueueWorkQueueItemOptions {
  queueId: string;
  name: string;
  /** Item payload; objects are serialized. Documented cap: 1 MB. */
  input?: Record<string, unknown> | string;
  /** Lower number = higher priority (1 is highest). */
  priority?: number;
  expiryDate?: string;
  /** The item cannot be dequeued before this time. */
  delayUntil?: string;
  /** Per-queue dedup key. */
  uniqueIdByQueue?: string;
}

function assertRunMode(value: string): asserts value is "attended" | "unattended" {
  if (value !== "attended" && value !== "unattended") {
    throw new ValidationError(`runMode must be "attended" or "unattended", got "${value}"`);
  }
}

/** Human phrase for a flowsession statuscode, with the raw code preserved. */
export function describeSessionStatus(statuscode: number | undefined): string {
  if (statuscode === undefined || statuscode === null) return "Unknown";
  const label = FLOW_SESSION_STATUS[statuscode];
  return label ? label : `Unknown status (${statuscode})`;
}

/** Human phrase for a flowmachine statuscode, with the raw code preserved. */
export function describeMachineStatus(statuscode: number | undefined): string {
  if (statuscode === undefined || statuscode === null) return "Unknown";
  const label = MACHINE_STATUS[statuscode];
  return label ? label : `Unknown status (${statuscode})`;
}

/** Client for the documented desktop-flows (RPA) Dataverse surface. */
export class DesktopFlowsApi {
  private readonly authProvider: AuthProvider;
  private environmentUrl: string;

  constructor(config: DesktopFlowsApiConfig) {
    this.authProvider = config.authProvider;
    this.environmentUrl = validateDataverseUrl(config.environmentUrl);
  }

  setEnvironmentUrl(url: string): void {
    this.environmentUrl = validateDataverseUrl(url);
  }

  private get origin(): string {
    return `https://${this.environmentUrl}`;
  }

  private async getAuthHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(this.origin);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      Accept: "application/json",
      ...extra,
    };
  }

  private buildUrl(path: string, query?: string): string {
    const base = `${this.origin}/api/data/${API_VERSION}/${path}`;
    return query ? `${base}?${query}` : base;
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
    privilegeHint: string,
  ): Promise<T> {
    if (response.statusCode === 429) {
      const retryAfter = response.headers["retry-after"];
      await response.body.text().catch(() => undefined);
      const seconds = retryAfter
        ? Number(Array.isArray(retryAfter) ? retryAfter[0] : retryAfter)
        : NaN;
      throw new RateLimitError(Number.isFinite(seconds) ? seconds * 1000 : undefined);
    }

    const { statusCode, text } = await readResponseBody(response, context);

    if (statusCode === 401) {
      throw new AuthenticationError(`${context}: Dataverse authentication failed`);
    }
    if (statusCode === 403) {
      throw new AuthorizationError(`${context}: access denied. ${privilegeHint}`);
    }
    if (statusCode === 404) {
      throw new ApiError(`${context}: not found`, 404, sanitizeErrorMessage(text));
    }
    if (statusCode >= 400) {
      let details: string;
      try {
        details = JSON.stringify(JSON.parse(text));
      } catch {
        details = text;
      }
      throw new ApiError(
        `${context}: Dataverse API error`,
        statusCode,
        sanitizeErrorMessage(details),
      );
    }

    if (!text) return {} as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError(`${context}: Dataverse returned a non-JSON response`, statusCode);
    }
  }

  /**
   * Fetch a file/raw column's `/$value` content as text. Desktop flows keep
   * their input/output schemas and run outputs in file columns; a flow with
   * no declared variables returns 404 or an empty body — both map to undefined.
   */
  private async getRawValue(path: string, context: string): Promise<string | undefined> {
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(path), { method: "GET", headers });
      if (response.statusCode === 404 || response.statusCode === 204) {
        await response.body.text().catch(() => undefined);
        return undefined;
      }
      const { statusCode, text } = await readResponseBody(response, context);
      if (statusCode >= 400) {
        // Schema/outputs are best-effort enrichments — callers degrade gracefully.
        logger.debug({ statusCode, context }, "Raw value fetch failed");
        return undefined;
      }
      return text || undefined;
    }, context);
  }

  private parseJsonOrUndefined(text: string | undefined, context: string): unknown {
    if (!text) return undefined;
    try {
      return JSON.parse(text);
    } catch {
      logger.debug({ context }, "Raw value was not valid JSON");
      return undefined;
    }
  }

  /** List desktop flows (workflow rows with category 6). */
  async listDesktopFlows(
    options: { includeDrafts?: boolean; top?: number } = {},
  ): Promise<DesktopFlowSummary[]> {
    const top = Math.min(Math.max(options.top ?? 50, 1), 250);
    const context = "List desktop flows";
    const query = [
      "$select=workflowid,name,description,modifiedon,statecode",
      "$filter=category eq 6",
      "$orderby=name asc",
      `$top=${top}`,
    ].join("&");
    // Unpublished (draft) desktop flows only return with this documented header.
    const headers = await this.getAuthHeaders(
      options.includeDrafts ? { "MSCRM.IncludeUnpublished": "true" } : undefined,
    );

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("workflows", query), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: DesktopFlowSummary[] }>(
        response,
        context,
        "Listing desktop flows requires Read privilege on the workflow (Process) table — Environment Maker or a Desktop Flows role grants it.",
      );
      return data.value ?? [];
    }, context);
  }

  /** Get one desktop flow plus its declared input/output schemas. */
  async getDesktopFlow(flowId: string): Promise<DesktopFlowDetail> {
    const safeId = validateRowId(flowId);
    const context = "Get desktop flow";
    const headers = await this.getAuthHeaders({ "MSCRM.IncludeUnpublished": "true" });

    const flow = await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(
          `workflows(${safeId})`,
          "$select=workflowid,name,description,modifiedon,createdon,statecode,category",
        ),
        { method: "GET", headers },
      );
      return this.handleResponse<DesktopFlowSummary & { category?: number }>(
        response,
        context,
        "Reading a desktop flow requires Read privilege on the workflow (Process) table.",
      );
    }, context);

    if (flow.category !== undefined && flow.category !== 6) {
      throw new ValidationError(
        `Workflow ${safeId} is not a desktop flow (category ${flow.category}). Use get_flow for cloud flows.`,
      );
    }

    const [inputText, outputText] = await Promise.all([
      this.getRawValue(`workflows(${safeId})/inputs/$value`, "Get desktop flow input schema"),
      this.getRawValue(`workflows(${safeId})/outputs/$value`, "Get desktop flow output schema"),
    ]);

    return {
      flow,
      inputSchema: this.parseJsonOrUndefined(inputText, "input schema"),
      outputSchema: this.parseJsonOrUndefined(outputText, "output schema"),
    };
  }

  /**
   * Trigger a desktop flow run via the documented RunDesktopFlow bound action.
   * Rate limit (documented): 70 runs/minute per connection.
   */
  async runDesktopFlow(options: RunDesktopFlowOptions): Promise<RunDesktopFlowResult> {
    const safeId = validateRowId(options.flowId);
    assertRunMode(options.runMode);
    const context = "Run desktop flow";

    if (!options.connectionName || !options.connectionName.trim()) {
      throw new ValidationError(
        "connectionName is required — a desktop-flows connection name, or a connection reference logical name with isConnectionReference: true. Use list_desktop_flow_connections to discover connection references.",
      );
    }

    const body: Record<string, unknown> = {
      runMode: options.runMode,
      connectionName: options.connectionName.trim(),
      runPriority: options.priority ?? "normal",
    };
    if (options.isConnectionReference) {
      // connectionType 2 = "connection reference logical name" (documented).
      body.connectionType = 2;
    }
    if (options.inputs !== undefined) {
      // The action expects a JSON-*string* parameter, not a JSON object.
      const serialized = JSON.stringify(options.inputs);
      if (Buffer.byteLength(serialized, "utf8") > MAX_RUN_INPUTS_BYTES) {
        throw new ValidationError("Desktop flow inputs exceed the documented 2 MB limit");
      }
      body.inputs = serialized;
    }
    if (options.timeoutSeconds !== undefined) {
      if (
        !Number.isInteger(options.timeoutSeconds) ||
        options.timeoutSeconds < 1 ||
        options.timeoutSeconds > MAX_RUN_TIMEOUT_SECONDS
      ) {
        throw new ValidationError(
          `timeoutSeconds must be an integer between 1 and ${MAX_RUN_TIMEOUT_SECONDS} (24 hours)`,
        );
      }
      body.timeout = options.timeoutSeconds;
    }
    if (options.callbackUrl !== undefined) {
      const parsed = (() => {
        try {
          return new URL(options.callbackUrl);
        } catch {
          throw new ValidationError("callbackUrl must be a valid URL");
        }
      })();
      // Documented callback constraints: HTTPS, DNS-named host, no localhost.
      if (parsed.protocol !== "https:") {
        throw new ValidationError("callbackUrl must use https");
      }
      if (
        parsed.hostname === "localhost" ||
        /^[0-9.]+$/.test(parsed.hostname) ||
        parsed.hostname.includes(":")
      ) {
        throw new ValidationError(
          "callbackUrl must use a public DNS hostname (Microsoft's callback rejects localhost and raw IP addresses)",
        );
      }
      body.callbackUrl = options.callbackUrl;
    }

    const headers = await this.getAuthHeaders();
    const result = await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(`workflows(${safeId})/Microsoft.Dynamics.CRM.RunDesktopFlow`),
        { method: "POST", headers, body: JSON.stringify(body) },
      );
      return this.handleResponse<{
        flowsessionId?: string;
        licenseCheck?: { code?: string; message?: string };
      }>(
        response,
        context,
        "Triggering a desktop flow requires Read privilege on the workflow table plus Create/Write on flowsession and workflowbinary. A Desktop Flows Machine User role (or higher) on a shared machine covers the machine side.",
      );
    }, context);

    if (!result.flowsessionId) {
      throw new ApiError(
        `${context}: Dataverse accepted the request but returned no flowsessionId`,
        502,
        sanitizeErrorMessage(JSON.stringify(result)),
      );
    }

    const licenseCode = result.licenseCheck?.code;
    const licenseWarning =
      licenseCode && licenseCode !== "ValidLicenseCoverage"
        ? (LICENSE_CHECK_GUIDANCE[licenseCode] ??
          `License check reported ${licenseCode}: ${result.licenseCheck?.message ?? "see the Power Automate licensing documentation"}`)
        : undefined;

    return { flowsessionId: result.flowsessionId, licenseWarning };
  }

  /** List desktop flow runs (flowsessions), optionally scoped to one flow. */
  async listRuns(options: { flowId?: string; top?: number } = {}): Promise<FlowSessionSummary[]> {
    const top = Math.min(Math.max(options.top ?? 25, 1), 250);
    const context = "List desktop flow runs";
    const parts = [
      "$select=flowsessionid,statuscode,statecode,startedon,completedon,errorcode,errormessage,gateway",
      "$orderby=startedon desc",
      `$top=${top}`,
    ];
    if (options.flowId) {
      const safeId = validateRowId(options.flowId);
      parts.push(`$filter=_regardingobjectid_value eq ${safeId}`);
    }
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("flowsessions", parts.join("&")), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: FlowSessionSummary[] }>(
        response,
        context,
        "Querying desktop flow runs requires Read privilege on the flowsession table. Runs are user-owned: you see your own runs; others' runs need shared access or an Owner-level role.",
      );
      return data.value ?? [];
    }, context);
  }

  /** Get one run's status and error detail; fetch outputs when it succeeded. */
  async getRun(sessionId: string): Promise<FlowSessionDetail> {
    const safeId = validateRowId(sessionId);
    const context = "Get desktop flow run";
    const headers = await this.getAuthHeaders();

    const session = await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(
          `flowsessions(${safeId})`,
          "$select=flowsessionid,statuscode,statecode,startedon,completedon,errorcode,errormessage,errordetails,gateway,context",
        ),
        { method: "GET", headers },
      );
      return this.handleResponse<FlowSessionSummary>(
        response,
        context,
        "Reading a desktop flow run requires Read privilege on the flowsession table (Owner-level for runs you did not trigger).",
      );
    }, context);

    let outputs: unknown;
    if (session.statuscode === 4) {
      const text = await this.getRawValue(
        `flowsessions(${safeId})/outputs/$value`,
        "Get desktop flow run outputs",
      );
      outputs = this.parseJsonOrUndefined(text, "run outputs");
    }

    return { session, outputs };
  }

  /** Cancel a queued or running desktop flow run (CancelDesktopFlowRun). */
  async cancelRun(sessionId: string): Promise<void> {
    const safeId = validateRowId(sessionId);
    const context = "Cancel desktop flow run";
    const headers = await this.getAuthHeaders();

    await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(`flowsessions(${safeId})/Microsoft.Dynamics.CRM.CancelDesktopFlowRun`),
        { method: "POST", headers, body: JSON.stringify({}) },
      );
      return this.handleResponse<Record<string, unknown>>(
        response,
        context,
        "Canceling a desktop flow run requires Owner-level privileges on the flowsession — runs you triggered qualify.",
      );
    }, context);
  }

  /** List solution connection references for the desktop-flows connector. */
  async listConnectionReferences(): Promise<DesktopFlowConnectionReference[]> {
    const context = "List desktop flow connection references";
    const query = [
      "$select=connectionreferenceid,connectionreferencelogicalname,connectionreferencedisplayname,connectionid,connectorid",
      "$filter=contains(connectorid,'shared_uiflow')",
      "$top=100",
    ].join("&");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("connectionreferences", query), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: DesktopFlowConnectionReference[] }>(
        response,
        context,
        "Listing connection references requires Read privilege on the connectionreference table (Environment Maker grants it).",
      );
      return data.value ?? [];
    }, context);
  }

  private static readonly MACHINE_PRIVILEGE_HINT =
    "Machine visibility requires Read privilege on the Flow Machine table — the Desktop Flows Machine Owner/User roles or Environment Maker grant it, and machines are user-owned records (you see machines you own or that are shared with you).";

  private static readonly MACHINE_LIST_SELECT =
    "flowmachineid,name,statecode,statuscode,lastheartbeatdate,sessioncapacity,hostingtype,agentversion,_flowmachinegroupid_value";

  /** List registered machines (flowmachine rows) visible to the caller. */
  async listMachines(options: { top?: number } = {}): Promise<FlowMachine[]> {
    const top = Math.min(Math.max(options.top ?? 100, 1), 250);
    const context = "List machines";
    const query = [
      `$select=${DesktopFlowsApi.MACHINE_LIST_SELECT}`,
      "$orderby=name asc",
      `$top=${top}`,
    ].join("&");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("flowmachines", query), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: FlowMachine[] }>(
        response,
        context,
        DesktopFlowsApi.MACHINE_PRIVILEGE_HINT,
      );
      return data.value ?? [];
    }, context);
  }

  /** Get one machine's full detail, including hosted-machine and key health. */
  async getMachine(machineId: string): Promise<FlowMachine> {
    const safeId = validateRowId(machineId);
    const context = "Get machine";
    const query = `$select=${DesktopFlowsApi.MACHINE_LIST_SELECT},hostedmachineerror,keydeliverystatus,overcapacitysince,createdon,modifiedon`;
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(`flowmachines(${safeId})`, query),
        { method: "GET", headers },
      );
      return this.handleResponse<FlowMachine>(
        response,
        context,
        DesktopFlowsApi.MACHINE_PRIVILEGE_HINT,
      );
    }, context);
  }

  /** List machine groups with a per-group count of member machines. */
  async listMachineGroups(options: { top?: number } = {}): Promise<FlowMachineGroupWithCount[]> {
    const top = Math.min(Math.max(options.top ?? 100, 1), 250);
    const context = "List machine groups";
    const query = [
      "$select=flowmachinegroupid,name,description,flowgrouptype,statecode,provisioningstate,provisioningerror,maxmanagedmachinecount,createdon",
      "$orderby=name asc",
      `$top=${top}`,
    ].join("&");
    const headers = await this.getAuthHeaders();

    const groups = await wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("flowmachinegroups", query), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: FlowMachineGroup[] }>(
        response,
        context,
        "Machine group visibility requires Read privilege on the Flow Machine Group table — the Desktop Flows roles or Environment Maker grant it.",
      );
      return data.value ?? [];
    }, context);

    // Membership counts come from the machines' group lookup — counted here
    // rather than $apply aggregation to stay on the plain documented query shape.
    const counts = new Map<string, number>();
    if (groups.length > 0) {
      const machines = await this.listMachines({ top: 250 });
      for (const machine of machines) {
        const groupId = machine._flowmachinegroupid_value;
        if (groupId) counts.set(groupId, (counts.get(groupId) ?? 0) + 1);
      }
    }

    return groups.map((group) => ({
      ...group,
      machineCount: counts.get(group.flowmachinegroupid) ?? 0,
    }));
  }

  /**
   * Restart a Microsoft-hosted machine (RestartHostedMachine bound action).
   * Refuses physical machines up front — the action only exists for hosted ones.
   */
  async restartHostedMachine(machineId: string): Promise<{ name?: string }> {
    const safeId = validateRowId(machineId);
    const context = "Restart hosted machine";

    const machine = await this.getMachine(safeId);
    const hostingLabel = MACHINE_HOSTING_TYPE[machine.hostingtype ?? 0];
    if (!hostingLabel) {
      throw new ValidationError(
        `Machine ${machine.name ?? safeId} is a physical (customer-managed) machine — RestartHostedMachine only applies to Microsoft-hosted machines. Restart physical machines at the OS level.`,
      );
    }

    const headers = await this.getAuthHeaders();
    await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(`flowmachines(${safeId})/Microsoft.Dynamics.CRM.RestartHostedMachine`),
        { method: "POST", headers, body: JSON.stringify({}) },
      );
      return this.handleResponse<Record<string, unknown>>(
        response,
        context,
        "Restarting a hosted machine requires owner-level access to the Flow Machine record.",
      );
    }, context);

    return { name: machine.name };
  }

  private static readonly WORK_QUEUE_PRIVILEGE_HINT =
    "Work queues need read/write privileges on the Work Queue and Work Queue Item tables — Environment Maker (with a Premium license) grants them.";

  /** List work queues. */
  async listWorkQueues(options: { top?: number } = {}): Promise<WorkQueue[]> {
    const top = Math.min(Math.max(options.top ?? 50, 1), 250);
    const context = "List work queues";
    const query = [
      "$select=workqueueid,name,workqueuekey,statecode,statuscode,itemmaxretrycount,itemmaxrequeuecount,defaultitemtimetoliveinminutes,workqueuetype,createdon",
      "$orderby=name asc",
      `$top=${top}`,
    ].join("&");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("workqueues", query), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: WorkQueue[] }>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
      return data.value ?? [];
    }, context);
  }

  /**
   * Count this queue's items in one statecode bucket.
   *
   * Uses the OData inline count (`$count=true` + `@odata.count`), NOT the
   * `/$count` path segment: Dataverse rejects a `$filter` there
   * (`Could not find a property named '_workqueueid_value' on type
   * 'Edm.Int32'`). Failures throw — a swallowed error here would report "0
   * queued" for a queue that is full, which is worse than no answer.
   */
  private async countItems(queueId: string, statecode: number): Promise<number> {
    const context = `Count work queue items (state ${statecode})`;
    const headers = await this.getAuthHeaders({ Prefer: "odata.include-annotations=\"*\"" });
    const url = this.buildUrl(
      "workqueueitems",
      `$filter=_workqueueid_value eq ${queueId} and statecode eq ${statecode}&$count=true&$top=1&$select=workqueueitemid`,
    );
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<{ "@odata.count"?: number }>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
      const n = data["@odata.count"];
      if (typeof n !== "number") {
        throw new ApiError(`${context}: Dataverse returned no @odata.count`, 502);
      }
      return n;
    }, context);
  }

  /** Get one work queue plus per-state item tallies. */
  async getWorkQueue(queueId: string): Promise<{ queue: WorkQueue; counts: WorkQueueItemCounts }> {
    const safeId = validateRowId(queueId);
    const context = "Get work queue";
    const headers = await this.getAuthHeaders();

    const queue = await wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(`workqueues(${safeId})`), {
        method: "GET",
        headers,
      });
      return this.handleResponse<WorkQueue>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
    }, context);

    const [queued, processing, processed, onHold, error] = await Promise.all([
      this.countItems(safeId, 0),
      this.countItems(safeId, 1),
      this.countItems(safeId, 2),
      this.countItems(safeId, 3),
      this.countItems(safeId, 4),
    ]);

    return { queue, counts: { queued, processing, processed, onHold, error } };
  }

  /** Create a work queue. */
  async createWorkQueue(options: CreateWorkQueueOptions): Promise<{ workqueueid?: string }> {
    const context = "Create work queue";
    if (!options.name.trim()) {
      throw new ValidationError("Work queue name must not be empty");
    }
    const body: Record<string, unknown> = {
      name: options.name.trim(),
      workqueuekey: (options.workqueueKey ?? options.name).trim(),
    };
    if (options.itemMaxRetryCount !== undefined) body.itemmaxretrycount = options.itemMaxRetryCount;
    if (options.itemMaxRequeueCount !== undefined)
      body.itemmaxrequeuecount = options.itemMaxRequeueCount;
    if (options.defaultItemTimeToLiveMinutes !== undefined)
      body.defaultitemtimetoliveinminutes = options.defaultItemTimeToLiveMinutes;
    if (options.inputSchema !== undefined) {
      body.inputschema = JSON.stringify(options.inputSchema);
      body.inputschematype = 1; // Json
    }

    const headers = await this.getAuthHeaders({ Prefer: "return=representation" });
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("workqueues"), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      return this.handleResponse<{ workqueueid?: string }>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
    }, context);
  }

  /** Enqueue one item onto a queue. */
  async enqueueWorkQueueItem(
    options: EnqueueWorkQueueItemOptions,
  ): Promise<{ workqueueitemid?: string }> {
    const safeQueueId = validateRowId(options.queueId);
    const context = "Enqueue work queue item";
    if (!options.name.trim()) {
      throw new ValidationError("Work queue item name must not be empty");
    }

    const body: Record<string, unknown> = {
      name: options.name.trim(),
      "workqueueid@odata.bind": `/workqueues(${safeQueueId})`,
    };
    if (options.input !== undefined) {
      const serialized =
        typeof options.input === "string" ? options.input : JSON.stringify(options.input);
      if (Buffer.byteLength(serialized, "utf8") > MAX_ITEM_INPUT_BYTES) {
        throw new ValidationError("Work queue item input exceeds the documented 1 MB limit");
      }
      body.input = serialized;
    }
    if (options.priority !== undefined) body.priority = options.priority;
    if (options.expiryDate !== undefined) body.expirydate = options.expiryDate;
    if (options.delayUntil !== undefined) body.delayuntil = options.delayUntil;
    if (options.uniqueIdByQueue !== undefined) body.uniqueidbyqueue = options.uniqueIdByQueue;

    const headers = await this.getAuthHeaders({ Prefer: "return=representation" });
    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("workqueueitems"), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      return this.handleResponse<{ workqueueitemid?: string }>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
    }, context);
  }

  /** List a queue's items, optionally filtered to one statecode bucket. */
  async listWorkQueueItems(options: {
    queueId: string;
    statecode?: number;
    top?: number;
  }): Promise<WorkQueueItem[]> {
    const safeQueueId = validateRowId(options.queueId);
    const top = Math.min(Math.max(options.top ?? 50, 1), 250);
    const context = "List work queue items";
    const filters = [`_workqueueid_value eq ${safeQueueId}`];
    if (options.statecode !== undefined) filters.push(`statecode eq ${options.statecode}`);
    const query = [
      "$select=workqueueitemid,name,statecode,statuscode,priority,retrycount,requeuecount,processingresult,processingstarttime,completedon,expirydate,delayuntil,uniqueidbyqueue",
      `$filter=${filters.join(" and ")}`,
      "$orderby=createdon desc",
      `$top=${top}`,
    ].join("&");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl("workqueueitems", query), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: WorkQueueItem[] }>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
      return data.value ?? [];
    }, context);
  }

  /**
   * Atomically claim the next queued item (documented Dequeue bound action):
   * oldest first honoring priority, flipped to Processing on return.
   */
  async dequeueWorkQueueItem(queueId: string): Promise<WorkQueueItem | undefined> {
    const safeQueueId = validateRowId(queueId);
    const context = "Dequeue work queue item";
    // The documented request shape: stringified JSON carrying FetchXML limited
    // to the workqueueitem table; the workqueueid condition is mandatory.
    // safeQueueId is UUID-validated above, so the template can't be escaped.
    const fetchXml = `<fetch mapping="logical" returntotalrecordcount="true" page="1" count="1" no-lock="false"><entity name="workqueueitem"><filter type="and"><condition attribute="workqueueid" operator="eq" value="${safeQueueId}"/><condition attribute="statuscode" operator="eq" value="0"/></filter></entity></fetch>`;
    const headers = await this.getAuthHeaders();

    const result = await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(`workqueues(${safeQueueId})/Microsoft.Dynamics.CRM.Dequeue`),
        {
          method: "POST",
          headers,
          body: JSON.stringify({ request: JSON.stringify({ query: fetchXml }) }),
        },
      );
      return this.handleResponse<WorkQueueItem | Record<string, never>>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
    }, context);

    if (!result || !("workqueueitemid" in result) || !result.workqueueitemid) {
      return undefined;
    }
    return result as WorkQueueItem;
  }

  /** Mark a claimed item processed/failed, or send it back to the queue. */
  async updateWorkQueueItem(options: {
    itemId: string;
    result: WorkQueueItemResult;
    processingResult?: string;
    delayUntil?: string;
  }): Promise<void> {
    const safeId = validateRowId(options.itemId);
    const context = "Update work queue item";
    const codes = WORK_QUEUE_ITEM_RESULTS[options.result];
    if (!codes) {
      throw new ValidationError(
        `result must be one of: ${Object.keys(WORK_QUEUE_ITEM_RESULTS).join(", ")}`,
      );
    }
    if (options.delayUntil !== undefined && options.result !== "requeue") {
      throw new ValidationError("delayUntil only applies when result is 'requeue'");
    }

    const body: Record<string, unknown> = { ...codes };
    if (options.processingResult !== undefined) body.processingresult = options.processingResult;
    if (options.delayUntil !== undefined) body.delayuntil = options.delayUntil;

    const headers = await this.getAuthHeaders();
    await wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(`workqueueitems(${safeId})`), {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      return this.handleResponse<Record<string, unknown>>(
        response,
        context,
        DesktopFlowsApi.WORK_QUEUE_PRIVILEGE_HINT,
      );
    }, context);
  }

  /** Permanently delete a work queue. */
  async deleteWorkQueue(queueId: string): Promise<void> {
    const safeId = validateRowId(queueId);
    const context = "Delete work queue";
    const headers = await this.getAuthHeaders();
    await wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(`workqueues(${safeId})`), {
        method: "DELETE",
        headers,
      });
      return this.handleResponse<Record<string, unknown>>(
        response,
        context,
        "Deleting a work queue requires Delete privilege on the Work Queue table (owner or admin).",
      );
    }, context);
  }

  /**
   * Fetch a run's action-level logs. Tries the documented V2 source first
   * (the `flowlog` elastic table via the flowsession relationship), then the
   * V1 `additionalcontext` file column. Microsoft documents V2 logs for
   * connector-triggered runs — API-triggered runs frequently have only V1 or
   * nothing, so the result names its source.
   */
  async getRunLogs(sessionId: string): Promise<RunLogsResult> {
    const safeId = validateRowId(sessionId);
    const context = "Get desktop flow run logs";
    const headers = await this.getAuthHeaders();

    const flowlogEntries = await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(
          `flowsessions(${safeId})/flowsession_flowlog_parentobjectid`,
          "$orderby=createdon asc",
        ),
        { method: "GET", headers },
      );
      // The relationship 404s on environments still on V1 logging — that is
      // an expected fallback path, not an error.
      if (response.statusCode === 404) {
        await response.body.text().catch(() => undefined);
        return undefined;
      }
      const data = await this.handleResponse<{ value: FlowLogEntry[] }>(
        response,
        context,
        "Reading run logs requires Read access to the parent flow session (Owner-level for runs you did not trigger).",
      );
      return data.value ?? [];
    }, context);

    if (flowlogEntries && flowlogEntries.length > 0) {
      return { source: "flowlog", entries: flowlogEntries };
    }

    const raw = await this.getRawValue(
      `flowsessions(${safeId})/additionalcontext/$value`,
      "Get desktop flow run V1 log context",
    );
    if (raw) {
      return { source: "additionalcontext", raw };
    }
    return { source: "none" };
  }

  /** Everything diagnose_desktop_flow_run needs, gathered best-effort. */
  async getRunDiagnostics(sessionId: string): Promise<{
    session: FlowSessionSummary;
    machine?: FlowMachine;
    logs: RunLogsResult;
  }> {
    const safeId = validateRowId(sessionId);
    const context = "Get desktop flow run diagnostics";
    const headers = await this.getAuthHeaders();

    const session = await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(
          `flowsessions(${safeId})`,
          "$select=flowsessionid,statuscode,statecode,startedon,completedon,errorcode,errormessage,errordetails,gateway,context,_machineid_value,_regardingobjectid_value",
        ),
        { method: "GET", headers },
      );
      return this.handleResponse<FlowSessionSummary & { _machineid_value?: string }>(
        response,
        context,
        "Diagnosing a run requires Read privilege on the flowsession (Owner-level for runs you did not trigger).",
      );
    }, context);

    // Machine health and logs are enrichments — a failure to fetch either
    // must not sink the diagnosis of the run itself.
    let machine: FlowMachine | undefined;
    if (session._machineid_value) {
      try {
        machine = await this.getMachine(session._machineid_value);
      } catch (error) {
        logger.debug(
          { error: error instanceof Error ? error.message : String(error) },
          "Diagnostics: machine lookup failed",
        );
      }
    }

    let logs: RunLogsResult = { source: "none" };
    try {
      logs = await this.getRunLogs(safeId);
    } catch (error) {
      logger.debug(
        { error: error instanceof Error ? error.message : String(error) },
        "Diagnostics: log fetch failed",
      );
    }

    return { session, machine, logs };
  }
}
